/**
 * Parses a hex color into its 8-bit RGB channels plus any alpha channel.
 *
 * Shared by `alpha` and `lighten`; not part of the package's public exports.
 * `alpha` ignores `a` because it replaces the alpha channel outright, while
 * `lighten` must carry it through — so it is returned here rather than dropped.
 *
 * @param color - `#rgb`, `#rgba`, `#rrggbb` or `#rrggbbaa` (leading `#` required)
 * @returns The channels — `a` is `undefined` for the 3/6-digit forms, and
 *   otherwise rounded to 3 decimals as MUI's `decomposeColor` does — or `null`
 *   when the input is not a parseable hex color
 */
export const parseHex = (
  color: string,
): { r: number; g: number; b: number; a: number | undefined } | null => {
  let hex = color.slice(1)
  if (hex.length === 3 || hex.length === 4) {
    hex = hex.replace(/./g, '$&$&')
  }
  if (hex.length !== 6 && hex.length !== 8) {
    return null
  }
  const int = Number.parseInt(hex.slice(0, 6), 16)
  if (Number.isNaN(int)) {
    return null
  }

  let a: number | undefined
  if (hex.length === 8) {
    const alphaInt = Number.parseInt(hex.slice(6, 8), 16)
    a = Number.isNaN(alphaInt) ? undefined : Number((alphaInt / 255).toFixed(3))
  }

  return { r: int >> 16, g: (int >> 8) & 255, b: int & 255, a }
}
